import { Alert } from "PFComponents/alert";
import { profileHash } from "PFCore/helpers/profile_hash";
import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";

import { getTranslationKey } from "../../../../helpers/custom_pptx_template";
import PagePreview from "../../../../parts/page_preview";
import { kpmgBlue } from "../../constants";
import { getKPMGClientsFieldNames, getKPMGQualificationsFieldNames, isKPMGCA } from "../../helpers";
import css from "./kpmg_pptx_preview.less";

const Positions = ({ positions, title }) => {
  if (!positions?.items || positions.items.length === 0) {
    return null;
  }

  return (
    <div className={css.positions}>
      <strong className={css.subheader} style={{ color: kpmgBlue }}>
        {title}
      </strong>
      <ul>
        {positions.items
          .filter(({ summary }) => summary)
          .map(({ id, summary }) => (
            <li key={id}>
              <div>{summary}</div>
            </li>
          ))}
      </ul>
    </div>
  );
};

const Contacts = ({ contactInformationItems, profile, templateKey }) => (
  <div className={css.contacts}>
    <strong className={css.subheader}>Contact</strong>
    {[
      { text: profile.email },
      ...contactInformationItems.filter(({ type }) =>
        isKPMGCA(templateKey) ? /Phone/.test(type) : /mobile_number/.test(type)
      )
    ].map((contact, index) => (
      <div key={`contract_information_${index}`}>{contact.text}</div>
    ))}
  </div>
);

const Summary = ({ summary, title }) => {
  if (!summary) {
    return null;
  }

  return (
    <>
      <strong className={css.subheader}>{title}</strong>
      <p className={css.summary}>{summary}</p>
    </>
  );
};

const CustomField = ({ customTypes, names, title }) => {
  const values = (names || []).reduce((acc, name) => {
    const customType = customTypes.find(({ type }) => type.name === name);
    if (customType) {
      const vals = customType.text ? customType.text.split("\r ") : [];
      return [...acc, ...vals];
    }
  }, []);

  if (values.length === 0) {
    return null;
  }

  return (
    <div className={css.customType}>
      <strong className={css.subheader}>{title}</strong>
      <ul>
        {values.map((item) => (
          <li key={item}>
            <div>{item}</div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const KpmgPptxPreview = ({ profile, order, config, avatar, logo }) => {
  const { templateKey, isBlindModeOn, hidden } = config;
  const { t } = useTranslation("profiles", { keyPrefix: "export" });

  const translationKey = getTranslationKey(templateKey);
  const qualificationsFiledNames = getKPMGQualificationsFieldNames(templateKey);
  const clientsFieldNames = getKPMGClientsFieldNames(templateKey);

  return (
    <>
      {profile.enabled === false && (
        <Alert kind="warning" title="Disabled" style={{ marginBottom: 20, marginTop: 20 }}>
          This profile won&apos;vt be exported when exporting all profiles at once because it&apos;vs disabled
          in the <b>Profiles</b> tab
        </Alert>
      )}
      <PagePreview
        style={{
          padding: "36px 59px 40px 59px",
          fontFamily: "arial",
          width: 720,
          height: 540,
          minHeight: 540,
          display: "flex"
        }}
      >
        <div className={css.sidebar}>
          {avatar && !isBlindModeOn && <img className={css.avatar} src={avatar} alt="avatar" />}
          <div className={css.sidebarDetails}>
            {!isBlindModeOn && (
              <Contacts
                contactInformationItems={profile.contactInformationItems}
                profile={profile}
                templateKey={templateKey}
              />
            )}

            <CustomField
              customTypes={profile.customTypes}
              names={order.filter((name) => qualificationsFiledNames.includes(name))}
              title={t(`${translationKey}.certificates`)}
            />
            <CustomField
              customTypes={profile.customTypes}
              names={order.filter((name) => clientsFieldNames.includes(name))}
              title={t(`${translationKey}.clients`)}
            />
          </div>
          <img className={css.logo} src={logo.url} height={33} alt="" />
        </div>
        <div className={css.main}>
          <div className={css.profileName}>
            {isBlindModeOn ? `Candidate ${profileHash(profile.id)}` : profile.name}
          </div>
          <strong className={css.jobTitle}>{profile.jobTitle}</strong>
          <div className={css.details}>
            {order.map((name) => {
              if (hidden[name]) {
                return false;
              } else if (name === "summary") {
                return (
                  <Summary summary={profile.summary} key={name} title={t(`${translationKey}.summary`)} />
                );
              } else if (name === "experience") {
                return (
                  <Positions
                    positions={profile.positions}
                    key={name}
                    title={t(`${translationKey}.positions`)}
                  />
                );
              }
            })}
          </div>
        </div>
      </PagePreview>
    </>
  );
};

const positionItemShape = PropTypes.shape({
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      jobTitle: PropTypes.string,
      companyName: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      current: PropTypes.bool,
      summary: PropTypes.string
    })
  )
});

Summary.propTypes = {
  summary: PropTypes.string,
  title: PropTypes.string
};

CustomField.propTypes = {
  customTypes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string
    })
  ),
  names: PropTypes.array,
  title: PropTypes.string
};

Contacts.propTypes = {
  contactInformationItems: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      text: PropTypes.string
    })
  ),
  profile: PropTypes.shape({
    email: PropTypes.string
  }),
  templateKey: PropTypes.string.isRequired
};

Positions.propTypes = {
  positions: positionItemShape,
  title: PropTypes.string
};

KpmgPptxPreview.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.number,
    enabled: PropTypes.bool,
    name: PropTypes.string,
    jobTitle: PropTypes.string,
    companyName: PropTypes.string,
    summary: PropTypes.string,
    contactInformationItems: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        text: PropTypes.string
      })
    ),
    positions: positionItemShape,
    customTypes: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string }))
  }),
  config: PropTypes.shape({
    color: PropTypes.string,
    hidden: PropTypes.object,
    logo: PropTypes.string,
    isBlindModeOn: PropTypes.bool,
    templateKey: PropTypes.string.isRequired
  }),
  order: PropTypes.arrayOf(PropTypes.string),
  avatar: PropTypes.string,
  logo: PropTypes.shape({
    url: PropTypes.string,
    custom: PropTypes.bool
  })
};

export default KpmgPptxPreview;
